import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Send, Paperclip, Plus, History, Trash2, FileText, X, Loader2, Bookmark, Zap, ChevronRight } from "lucide-react";
import MickeyAIIcon from "./MickeyAIIcon";
import { setMickeyMood, useMickeyMood } from "./mickeyMood";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { aiApi, roleAI, absUrl } from "./aiApi";
import AIMarkdown from "./AIMarkdown";
import AIActionCard from "./AIActionCard";
import AIImageJobCard from "./AIImageJobCard";
import AISkillsMenu from "./AISkillsMenu";
import AIDocFilesCard from "./AIDocFilesCard";
import AIAttachTray from "./AIAttachTray";
import AIDocStylePicker from "./AIDocStylePicker";
import AIScanButton from "./AIScanButton";
import AIWordPreview from "./AIWordPreview";
import AIFilesPanel from "./AIFilesPanel";

const THINKING = ["Menyemak data...", "Menganalisa rekod...", "Menyusun jawapan..."];

export default function AIChat({ user, summary }) {
  const ai = roleAI(user.role);
  const [cid, setCid] = useState(() => sessionStorage.getItem("ai_cid") || null);
  const [messages, setMessages] = useState([]);
  const [convs, setConvs] = useState([]);
  const [text, setText] = useState("");
  const [files, setFiles] = useState([]);
  const [docStyle, setDocStyle] = useState(() => localStorage.getItem("ai_doc_style") || "biasa");
  const pickStyle = (s) => { setDocStyle(s); localStorage.setItem("ai_doc_style", s); };
  const imgCount = files.filter((f) => f.mime?.startsWith("image/")).length;
  const [uploading, setUploading] = useState(0);
  const [sending, setSending] = useState(false);
  const mood = useMickeyMood();
  const [thinkIdx, setThinkIdx] = useState(0);
  const [preview, setPreview] = useState(null);
  const [wordView, setWordView] = useState(null);
  const endRef = useRef(null);
  const fileRef = useRef(null);
  const canUpload = summary?.can_upload;
  const [streamText, setStreamText] = useState("");
  const [toolLabel, setToolLabel] = useState("");
  const [prompts, setPrompts] = useState({ defaults: [], saved: [] });
  const skipLoad = useRef(false);
  const hasPrompts = ["superadmin", "branch_manager"].includes(user.role);

  useEffect(() => { if (hasPrompts) aiApi.quickPrompts().then(setPrompts).catch(() => {}); }, [hasPrompts]);
  const savePrompt = async () => {
    const t = text.trim();
    if (t.length < 2) { toast.error("Taip arahan dahulu untuk disimpan"); return; }
    try { const d = await aiApi.addQuickPrompt(t); setPrompts((p) => ({ ...p, saved: [...p.saved, d] })); toast.success("Arahan pantas disimpan"); }
    catch (e) { toast.error(e.response?.data?.detail || "Gagal simpan"); }
  };
  const delPrompt = async (id) => {
    await aiApi.deleteQuickPrompt(id).catch(() => {});
    setPrompts((p) => ({ ...p, saved: p.saved.filter((x) => x.id !== id) }));
  };

  useEffect(() => {
    if (!cid) { setMessages([]); return; }
    sessionStorage.setItem("ai_cid", cid);
    if (skipLoad.current) { skipLoad.current = false; return; }
    aiApi.messages(cid).then((d) => setMessages(d.messages)).catch(() => { setCid(null); sessionStorage.removeItem("ai_cid"); });
  }, [cid]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" }); }, [messages, sending, streamText]);
  useEffect(() => {
    if (!sending) return;
    const t = setInterval(() => setThinkIdx((i) => (i + 1) % THINKING.length), 1800);
    return () => clearInterval(t);
  }, [sending]);

  const loadConvs = () => aiApi.conversations().then(setConvs).catch(() => {});
  const newChat = () => { setCid(null); sessionStorage.removeItem("ai_cid"); setMessages([]); };

  const onPick = async (e) => {
    const list = Array.from(e.target.files || []);
    e.target.value = "";
    for (const f of list.slice(0, Math.max(0, 10 - files.length))) {
      try {
        setUploading(1);
        const saved = await aiApi.upload(f, (p) => setUploading(p || 1));
        setFiles((x) => [...x, saved]);
      } catch (err) {
        toast.error(err.response?.data?.detail || `Gagal muat naik ${f.name}`);
      } finally { setUploading(0); }
    }
  };

  const send = async (msg, override) => {
    const m = (msg ?? text).trim();
    const useFiles = override || files;
    if ((!m && !useFiles.length) || sending) return;
    const temp = { id: `tmp-${Date.now()}`, role: "user", content: m, attachments: useFiles };
    setMessages((x) => [...x, temp]);
    if (!override) setText("");
    const atts = useFiles.map((f) => f.id);
    if (!override) setFiles([]);
    setSending(true);
    setMickeyMood("thinking");
    setStreamText("");
    setToolLabel("");
    let started = false;
    let done = false;
    const payload = { message: m, conversation_id: cid, attachment_ids: atts, doc_style: atts.length ? docStyle : null };
    try {
      await aiApi.chatStream(payload, (ev) => {
        if (ev.type === "start") {
          started = true;
          setMessages((x) => [...x.filter((y) => y.id !== temp.id), ev.user_message]);
        } else if (ev.type === "delta") {
          setStreamText((t) => t + ev.text);
        } else if (ev.type === "reset") {
          setStreamText("");
        } else if (ev.type === "tool") {
          setToolLabel(ev.label || "Menyemak data");
        } else if (ev.type === "done") {
          done = true;
          setStreamText("");
          setMessages((x) => [...x, ev.reply]);
          if (!cid) { skipLoad.current = true; setCid(ev.conversation_id); }
          if (ev.reply?.refresh) aiApi.messages(ev.conversation_id).then((d) => setMessages(d.messages)).catch(() => {});
        } else if (ev.type === "error") {
          toast.error(ev.message || "AI gagal menjawab");
        }
      });
      if (!done && started) throw Object.assign(new Error("Sambungan terputus. Sila buka semula perbualan."), { started: true });
      if (!started) throw new Error("no-stream");
      setMickeyMood("happy", 1800);
    } catch (e) {
      if (!started && e.status !== 401 && e.status !== 403 && e.status !== 404) {
        // Jatuh balik ke mod tanpa strim (cth. proksi menyekat SSE)
        try {
          const d = await aiApi.chat(payload);
          if (!cid) { skipLoad.current = true; setCid(d.conversation_id); }
          setMessages((x) => [...x.filter((y) => y.id !== temp.id), d.user_message, d.reply]);
          if (d.reply?.refresh) aiApi.messages(d.conversation_id).then((r) => setMessages(r.messages)).catch(() => {});
          setMickeyMood("happy", 1800);
          return;
        } catch (e2) { e = e2; }
      }
      setMickeyMood("idle");
      toast.error(e.response?.data?.detail || (e.message !== "no-stream" && e.message) || "AI gagal menjawab");
      if (!started) { setMessages((x) => x.filter((y) => y.id !== temp.id)); setText(m); }
    } finally { setSending(false); setStreamText(""); setToolLabel(""); }
  };

  const del = async (id) => {
    await aiApi.deleteConversation(id).catch(() => {});
    if (id === cid) newChat();
    loadConvs();
  };
  const [askAll, setAskAll] = useState(false);
  const delAll = async () => {
    try { const r = await aiApi.deleteAllConversations(); toast.success(`${r.deleted} perbualan dipadam`); newChat(); setConvs([]); }
    catch (e) { toast.error(e.response?.data?.detail || "Gagal padam"); } finally { setAskAll(false); }
  };
  const [resolving, setResolving] = useState(null);
  const resolveAll = async (m, ok) => {
    setResolving(m.id);
    try {
      const note = await aiApi.resolveAll(m.actions.filter((a) => a.status === "pending").map((a) => a.id), ok);
      const d = await aiApi.messages(note.conversation_id || cid);
      setMessages(d.messages);
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal"); } finally { setResolving(null); }
  };
  const scanToWord = (up) => send(`Salin semua ${up.length} halaman imbasan ini (ikut susunan halaman) ke SATU dokumen Word (.docx) yang boleh diedit — transkripsi teks penuh, kekalkan tajuk, perenggan & jadual.${docStyle === "surat_rasmi" ? " Guna gaya surat rasmi Destiny Junior." : ""}`, up);

  return (
    <div className="flex flex-col h-full min-h-0">
      <div className="flex items-center gap-2 px-3 py-2 border-b border-slate-100 bg-white/90 backdrop-blur-sm">
        <button onClick={newChat} className={`h-8 px-3 rounded-full ${ai.soft} ${ai.text} text-xs font-bold flex items-center gap-1.5 hover:brightness-95 transition-[filter]`} data-testid="ai-new-chat"><Plus className="h-4 w-4" /> Baharu</button>
        <Popover onOpenChange={(o) => o && loadConvs()}>
          <PopoverTrigger asChild>
            <button className="h-8 px-3 rounded-full border border-slate-200 bg-white text-slate-600 text-xs font-semibold flex items-center gap-1.5 hover:bg-slate-50 transition-colors" data-testid="ai-history-btn"><History className="h-4 w-4" /> Sejarah</button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-72 p-1 max-h-80 overflow-y-auto z-[10001]">
            {convs.length === 0 && <div className="text-xs text-slate-400 p-3">Tiada perbualan lagi</div>}
            {convs.length > 0 && (askAll ? (
              <div className="flex items-center gap-2 rounded-lg bg-rose-50 px-2 py-1.5 mb-1">
                <span className="flex-1 text-[11px] font-semibold text-rose-700">Padam SEMUA {convs.length} perbualan?</span>
                <button className="text-[11px] text-slate-500" onClick={() => setAskAll(false)} data-testid="ai-history-delete-all-no">Tidak</button>
                <button className="text-[11px] font-bold text-rose-600" onClick={delAll} data-testid="ai-history-delete-all-yes">Ya</button>
              </div>
            ) : (
              <button onClick={() => setAskAll(true)} className="w-full text-left text-[11px] font-semibold text-rose-500 hover:bg-rose-50 rounded-lg px-2 py-1.5 mb-1 flex items-center gap-1" data-testid="ai-history-delete-all"><Trash2 className="h-3 w-3" /> Padam semua sejarah</button>
            ))}
            {convs.map((c) => (
              <div key={c.id} className={`group flex items-center gap-1 rounded-lg px-2 py-1.5 hover:bg-slate-50 ${c.id === cid ? "bg-slate-100" : ""}`}>
                <button className="flex-1 text-left text-xs truncate text-slate-700" onClick={() => setCid(c.id)} data-testid="ai-history-item">{c.title}</button>
                <button onClick={() => del(c.id)} className="opacity-60 hover:opacity-100 text-rose-500" aria-label="Padam" data-testid="ai-history-delete"><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
            ))}
          </PopoverContent>
        </Popover>
        {canUpload && <AIFilesPanel onOpenDoc={setWordView} onPreview={setPreview} cid={cid}
          onCreated={(d) => { if (!cid) { skipLoad.current = true; setCid(d.conversation_id); } setMessages((x) => [...x, d.user_message, d.reply]); }} />}
        <AISkillsMenu cid={cid} canUpload={canUpload} role={user.role} onAsk={(m) => send(m, [])}
          onCreated={(d) => { if (!cid) { skipLoad.current = true; setCid(d.conversation_id); } setMessages((x) => [...x, d.user_message, d.reply]); }} />
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto px-3 py-4 space-y-4 overscroll-contain bg-slate-50/60" data-testid="ai-messages">
        {messages.length === 0 && !sending && (
          <div className="text-center pt-6 px-2">
            <div className="mk-hover mx-auto h-20 w-20 drop-shadow-[0_8px_12px_rgba(236,72,153,0.35)]" data-testid="ai-chat-mickey"><MickeyAIIcon role={user.role} mood={mood} className="h-20 w-20" /></div>
            <div className="mt-3 font-extrabold text-slate-800">Hai {user.name?.split(" ")[0]}! Saya {ai.name}.</div>
            <div className={`text-[11px] font-bold mt-0.5 ${ai.text}`}>{ai.roleLabel} anda</div>
            <div className="text-xs text-slate-500 mt-1">Tanya apa sahaja dalam skop anda. Tindakan penting akan dipaparkan untuk anda sahkan dahulu.</div>
            <div className="mt-4 grid gap-2">
              {ai.suggestions.map((s) => (
                <button key={s} onClick={() => send(s)} className={`group flex items-center gap-2 text-left text-[13px] rounded-2xl px-3.5 py-2.5 bg-white border border-slate-100 shadow-sm ${ai.text} font-semibold hover:-translate-y-0.5 hover:shadow-md transition-[transform,box-shadow] duration-200`} data-testid="ai-suggestion">
                  <span className="flex-1">{s}</span><ChevronRight className="h-4 w-4 shrink-0 opacity-50 group-hover:opacity-100 transition-opacity" />
                </button>
              ))}
            </div>
          </div>
        )}
        {messages.map((m) => (
          <div key={m.id} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            <div className={`${m.role === "user" ? `max-w-[85%] rounded-[20px] rounded-br-md bg-gradient-to-br ${ai.grad} text-white px-3.5 py-2 shadow-md` : "w-full max-w-full"}`}>
              {m.role === "user" ? (
                <>
                  {m.content && <div className="text-[13.5px] whitespace-pre-wrap">{m.content}</div>}
                  {(m.attachments || []).map((a) => (
                    a.mime?.startsWith("image/") ? <img key={a.id} src={absUrl(a.url)} alt={a.filename} className="mt-1 h-24 rounded-lg object-cover" />
                      : a.mime?.includes("wordprocessingml") ? (
                        <button key={a.id} onClick={() => setWordView({ url: a.url, filename: a.filename })} className="mt-1 flex items-center gap-1 text-xs bg-white/20 hover:bg-white/30 rounded-lg px-2 py-1 transition-colors" data-testid="ai-attach-open-docx">
                          <FileText className="h-3.5 w-3.5" /> {a.filename} <span className="ml-1 rounded bg-white/30 px-1 text-[10px] font-bold">Buka</span>
                        </button>
                      )
                      : <div key={a.id} className="mt-1 flex items-center gap-1 text-xs bg-white/20 rounded-lg px-2 py-1"><FileText className="h-3.5 w-3.5" /> {a.filename}</div>
                  ))}
                </>
              ) : (
                <div className={`rounded-[20px] rounded-tl-md border px-4 py-3 shadow-[0_2px_12px_rgba(15,23,42,0.05)] ${m.system_note ? "border-emerald-200 bg-emerald-50/60" : "border-slate-100 bg-white"}`} data-testid="ai-reply">
                  <AIMarkdown onImage={setPreview} onOpenDoc={setWordView}>{m.content}</AIMarkdown>
                  {(m.image_jobs || []).map((j) => (
                    <AIImageJobCard key={j} jobId={j} onPreview={setPreview}
                      onNewJob={(nid) => setMessages((x) => x.map((y) => (y.id === m.id ? { ...y, image_jobs: [...(y.image_jobs || []), nid] } : y)))} />
                  ))}
                  {m.doc_files?.length > 0 && (
                    <AIDocFilesCard files={m.doc_files} jobId={m.doc_job_id} onRegenerated={(d) => setMessages((x) => [...x, d.user_message, d.reply])} />
                  )}
                  {(m.actions || []).filter((a) => a.status === "pending").length > 1 && (
                    <div className="mt-2 flex items-center gap-2 rounded-2xl border border-emerald-200 bg-emerald-50/70 p-2" data-testid="ai-resolve-all">
                      <span className="flex-1 text-[12px] font-semibold text-emerald-800">{(m.actions || []).filter((a) => a.status === "pending").length} tugasan menunggu pengesahan</span>
                      <Button size="sm" variant="ghost" className="h-8 rounded-xl text-xs" onClick={() => resolveAll(m, false)} disabled={resolving === m.id} data-testid="ai-cancel-all-btn">Batal semua</Button>
                      <Button size="sm" className="h-8 rounded-xl text-xs bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => resolveAll(m, true)} disabled={resolving === m.id} data-testid="ai-confirm-all-btn">
                        {resolving === m.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Sahkan semua"}
                      </Button>
                    </div>
                  )}
                  {(m.actions || []).map((a) => (
                    <AIActionCard key={`${a.id}-${a.status}`} action={a} user={user} onDone={(note) => note && setMessages((x) => [...x, note])} />
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}
        {sending && streamText && (
          <div className="flex justify-start" data-testid="ai-streaming">
            <div className="w-full rounded-[20px] rounded-tl-md bg-white border border-slate-100 px-4 py-3 shadow-[0_2px_12px_rgba(15,23,42,0.05)]">
              <AIMarkdown onImage={setPreview}>{streamText}</AIMarkdown>
              <span className="inline-block h-3.5 w-1.5 bg-fuchsia-400 rounded-sm animate-pulse align-middle" />
            </div>
          </div>
        )}
        {sending && !streamText && (
          <div className="flex items-center gap-2 text-xs text-slate-500 pl-1" data-testid="ai-thinking">
            <span className="h-11 w-11 shrink-0 drop-shadow-[0_4px_6px_rgba(236,72,153,0.3)]" data-testid="ai-thinking-mickey"><MickeyAIIcon role={user.role} mood="thinking" className="h-11 w-11" /></span>
            <span className="rounded-2xl rounded-bl-md bg-white border border-slate-100 px-3 py-1.5 shadow-sm">{toolLabel ? `${toolLabel}...` : THINKING[thinkIdx]}</span>
          </div>
        )}
        <div ref={endRef} />
      </div>

      <div className="border-t border-slate-100 bg-white p-2.5 shadow-[0_-4px_20px_rgba(15,23,42,0.04)]" style={{ paddingBottom: "max(10px, env(safe-area-inset-bottom))" }}>
        {files.length > 0 && <AIAttachTray files={files} setFiles={setFiles} />}
        {imgCount > 0 && (
          <AIDocStylePicker value={docStyle} onChange={pickStyle} count={imgCount} disabled={sending || uploading > 0}
            onCopy={() => send(`Salin ${imgCount > 1 ? `semua ${imgCount} gambar ini ikut susunan halaman` : "gambar ini"} ke satu dokumen Word dan PDF${docStyle === "surat_rasmi" ? " berkepala surat rasmi Destiny Junior" : ""}.`)} />
        )}
        {uploading > 0 && <div className="text-[11px] text-slate-500 flex items-center gap-1 mb-2"><Loader2 className="h-3 w-3 animate-spin" /> Memuat naik {uploading}%</div>}
        {hasPrompts && (prompts.saved.length > 0 || prompts.defaults.length > 0) && (
          <div className="flex gap-1 overflow-x-auto pb-1.5 mb-1 no-scrollbar" data-testid="ai-quick-prompts">
            {prompts.saved.map((p) => (
              <span key={p.id} className={`group shrink-0 flex items-center gap-1 rounded-full border border-fuchsia-200 ${ai.soft} pl-2 pr-1 py-0.5`}>
                <button onClick={() => send(p.text)} disabled={sending} className={`text-[11px] font-semibold ${ai.text} max-w-[180px] truncate`} data-testid="ai-quick-prompt-saved">
                  <Bookmark className="inline h-3 w-3 mr-0.5 -mt-0.5" />{p.text}
                </button>
                <button onClick={() => delPrompt(p.id)} className="text-slate-400 hover:text-rose-500" aria-label="Padam arahan"><X className="h-3 w-3" /></button>
              </span>
            ))}
            {prompts.defaults.map((t) => (
              <button key={t} onClick={() => send(t)} disabled={sending} data-testid="ai-quick-prompt"
                className="shrink-0 rounded-full border border-slate-200 bg-white px-2.5 py-0.5 text-[11px] font-semibold text-slate-600 hover:border-fuchsia-300 hover:text-fuchsia-700 transition-colors">
                <Zap className="inline h-3 w-3 mr-0.5 -mt-0.5 text-amber-500" />{t}
              </button>
            ))}
          </div>
        )}
        <div className={`flex items-end gap-1 rounded-2xl border border-slate-200 bg-slate-50/80 p-1 focus-within:bg-white focus-within:ring-2 ${ai.focus} transition-[background-color,box-shadow,border-color] duration-200`} data-testid="ai-composer">
          {hasPrompts && (
            <Button size="icon" variant="ghost" className="h-10 w-9 rounded-xl shrink-0 text-slate-400 hover:text-fuchsia-600" onClick={savePrompt} disabled={sending || !text.trim()} title="Simpan sebagai arahan pantas" data-testid="ai-save-prompt-btn">
              <Bookmark className="h-4 w-4" />
            </Button>
          )}
          {canUpload && (
            <>
              <input ref={fileRef} type="file" accept="image/*,application/pdf,.doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document" multiple hidden onChange={onPick} data-testid="ai-file-input" />
              <Button size="icon" variant="ghost" className="h-10 w-10 rounded-xl shrink-0" onClick={() => fileRef.current?.click()} disabled={sending} data-testid="ai-attach-btn" title="Lampir gambar/PDF">
                <Paperclip className="h-5 w-5" />
              </Button>
              <AIScanButton disabled={sending || files.length >= 10} remaining={10 - files.length} onAdd={(f) => setFiles((x) => [...x, f].slice(0, 10))} onToWord={scanToWord} />
            </>
          )}
          <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={1} placeholder={`Tanya ${ai.name}...`}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
            className="min-h-[40px] max-h-28 resize-none border-0 bg-transparent shadow-none px-2 text-[13.5px] placeholder:text-slate-400 focus-visible:ring-0 focus-visible:ring-offset-0" data-testid="ai-input" />
          <Button size="icon" onClick={() => send()} disabled={sending || (!text.trim() && !files.length)}
            className={`h-10 w-10 rounded-xl shrink-0 bg-gradient-to-br ${ai.grad} text-white shadow-md hover:scale-105 active:scale-95 transition-transform disabled:opacity-40`} data-testid="ai-send-btn">
            {sending ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />}
          </Button>
        </div>
      </div>

      <AIWordPreview file={wordView} onClose={() => setWordView(null)} />
      {preview && (
        <div className="fixed inset-0 z-[10003] bg-slate-900/80 backdrop-blur-sm flex flex-col items-center justify-center p-4" onClick={() => setPreview(null)} data-testid="ai-image-lightbox">
          <img src={preview} alt="pratonton" className="max-w-full max-h-[80vh] object-contain rounded-xl shadow-2xl" onClick={(e) => e.stopPropagation()} />
          <div className="mt-3 flex gap-3">
            <a href={preview} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="text-xs font-bold text-white bg-white/20 hover:bg-white/30 rounded-full px-4 py-2">Buka / muat turun</a>
            <button className="text-xs font-bold text-white bg-white/20 hover:bg-white/30 rounded-full px-4 py-2">Tutup</button>
          </div>
        </div>
      )}
    </div>
  );
}
